import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { imgUrl, label, fmtDate } from "../lib/api";
import { STATUS_TONE } from "../lib/portals";
import { Loader2 } from "lucide-react";

export const PageHeader = ({ title, sub, children, testid }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between tro-fade" data-testid={testid || "page-header"}>
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-[#17191C] md:text-3xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-[#6C757D]">{sub}</p>}
    </div>
    {children && <div className="flex min-w-0 flex-wrap gap-2">{children}</div>}
  </div>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section className={`tro-card p-4 md:p-5 ${className}`} data-testid={testid}>
    {(title || action) && (
      <div className="mb-3 flex items-center justify-between gap-2">
        {title && <h2 className="text-base font-bold text-[#17191C] md:text-lg">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const StatCard = ({ label: l, value, testid }) => (
  <div className="tro-card relative overflow-hidden p-4" data-testid={testid}>
    <div className="absolute inset-x-0 top-0 h-1 tro-metal" />
    <div className="tro-label">{l}</div>
    <div className="mt-2 text-3xl font-extrabold tracking-tight text-[#17191C]">{value}</div>
  </div>
);

export const StatusBadge = ({ status, testid }) => {
  const tone = STATUS_TONE[status] || "muted";
  const cls = {
    live: "bg-[#1E7E34] text-white", warn: "bg-[#FFD400] text-[#17191C]", red: "bg-[#F02030] text-white",
    ok: "bg-[#17191C] text-white", muted: "bg-[#E6E9ED] text-[#343A40]",
  }[tone];
  return (
    <span data-testid={testid || "status-badge"} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${cls}`}>
      {tone === "live" && <span className="live-dot h-1.5 w-1.5 rounded-full bg-white" />}
      {label(status)}
    </span>
  );
};

export const Crest = ({ club, size = 40, testid }) => {
  const instance = useId();
  const [failed, setFailed] = useState(null);
  const src = imgUrl(club?.logo);
  const showLogo = src && failed !== src;
  const id = testid || `team-crest-${instance.replace(/:/g, "")}`;
  const initials = club?.crest_text || club?.short || club?.short_name || club?.name?.split(/\s+/).map((s) => s[0]).join("").slice(0, 3) || "?";
  return (
    <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[#C5CBD1] font-extrabold text-white"
      data-testid={id} style={{ width: size, height: size, background: showLogo ? "#FFFFFF" : club?.crest_color || "#343A40", fontSize: size * 0.28 }}>
      {showLogo ? <img src={src} alt={`${club?.club_name || club?.name || "Team"}${club?.team_name ? ` ${club.team_name}` : ""} logo`} className="h-full w-full object-contain p-0.5" onError={() => setFailed(src)} data-testid={`${id}-image`} /> : <span data-testid={`${id}-initials`}>{initials}</span>}
    </div>
  );
};

export const IconBox = ({ icon: Icon, size = "md", active, tone, className = "", testid }) => (
  <span data-testid={testid} className={`tro-icon ${size === "sm" ? "tro-icon-sm" : size === "nav" ? "tro-icon-nav" : ""} ${active ? "tro-icon-active" : ""} ${tone ? `tro-icon-${tone}` : ""} ${className}`}>
    <Icon strokeWidth={2} />
  </span>
);

export const Avatar = ({ player, size = 44, ring }) => {
  const src = imgUrl(player?.photo);
  return (
    <div className="relative shrink-0 overflow-hidden bg-[#E7EAEE]" style={{ width: size, height: size, borderRadius: size * 0.3, boxShadow: ring ? `0 0 0 3px ${ring}` : "0 0 0 2px #fff" }}>
      {src ? <img src={src} alt={player?.short_name} className="h-full w-full object-cover" /> :
        <div className="flex h-full w-full items-center justify-center text-xs font-bold text-[#343A40]">{player?.number ?? "?"}</div>}
    </div>
  );
};

export const Empty = ({ text = "Nothing here yet.", testid }) => (
  <div className="rounded-lg border border-dashed border-[#C5CBD1] p-6 text-center text-sm text-[#6C757D]" data-testid={testid || "empty-state"}>{text}</div>
);

export const Spinner = () => (
  <div className="flex items-center justify-center p-10 text-[#6C757D]" data-testid="loading"><Loader2 className="h-6 w-6 animate-spin" /></div>
);

export const DemoTag = () => (
  <span className="rounded border border-[#C5CBD1] bg-[#F7F8F9] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#6C757D]" data-testid="demo-tag">Demo data</span>
);

export const Table = ({ cols, rows, testid, empty }) =>
  !rows?.length ? <Empty text={empty} /> : (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E6E9ED] text-left">
            {cols.map((c) => <th key={c[0]} className="whitespace-nowrap px-3 py-2 tro-label">{c[0]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-[#F7F8F9] transition-colors hover:bg-[#F7F8F9]">
              {cols.map((c) => <td key={c[0]} className="whitespace-nowrap px-3 py-2.5 text-[#343A40]">{c[1](r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

export const MatchRow = ({ m, to, right }) => (
  <Link to={to || `/matches/${m.id}`} data-testid={`match-row-${m.match_number}`}
    className="flex items-center gap-3 rounded-lg border border-[#E6E9ED] bg-white p-3 transition-colors hover:border-[#C5CBD1] hover:bg-[#F7F8F9]">
    <div className="hidden w-24 shrink-0 text-xs text-[#6C757D] sm:block">
      <div className="font-mono font-bold text-[#343A40]">{m.match_number}</div>
      <div>{fmtDate(m.kickoff)}</div>
    </div>
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2 text-right">
      <span className="truncate text-sm font-bold">{m.home.name}</span><Crest club={m.home} size={28} />
    </div>
    <div className="w-16 text-center font-mono text-lg font-extrabold">{m.score.home}–{m.score.away}</div>
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <Crest club={m.away} size={28} /><span className="truncate text-sm font-bold">{m.away.name}</span>
    </div>
    <div className="hidden shrink-0 md:block">{right || <StatusBadge status={m.status} />}</div>
  </Link>
);
